import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getGlamours, createGlamour, updateGlamour, deleteGlamour, getRandomGlamour, extractErrorMessage } from '../services/api';
import { FaPlus, FaEdit, FaTrash, FaDice, FaTshirt } from 'react-icons/fa';
import { useToast } from '../components/Toast';
import '../styles/Glamours.css';

function Glamours({ user }) {
  const toast = useToast();
  const [glamours, setGlamours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingGlamour, setEditingGlamour] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [randomGlamour, setRandomGlamour] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    occasion: '',
    character_id: '',
    items: '',
    notes: '',
    image_url: ''
  });

  useEffect(() => {
    fetchGlamours();
  }, [filter]);

  const fetchGlamours = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? { occasion: filter } : {};
      const data = await getGlamours(params);
      setGlamours(data.glamours || data);
    } catch (error) {
      console.error('Error fetching glamours:', error);
      toast.error(extractErrorMessage(error, 'Glamours konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setFormError(null);
    try {
      const payload = {
        ...formData,
        items: formData.items ? formData.items.split('\n').filter(i => i.trim()) : []
      };
      if (editingGlamour) {
        await updateGlamour(editingGlamour.id, payload);
      } else {
        await createGlamour(payload);
      }
      setShowModal(false);
      resetForm();
      toast.success(editingGlamour ? 'Glamour aktualisiert' : 'Glamour erstellt');
      fetchGlamours();
    } catch (error) {
      console.error('Error saving glamour:', error);
      setFormError(extractErrorMessage(error, 'Fehler beim Speichern des Glamours'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (glamour) => {
    setEditingGlamour(glamour);
    setFormData({
      name: glamour.name || '',
      occasion: glamour.occasion || '',
      character_id: glamour.character_id || '',
      items: Array.isArray(glamour.items) ? glamour.items.join('\n') : (glamour.items || ''),
      notes: glamour.notes || '',
      image_url: glamour.image_url || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Glamour wirklich löschen?')) return;
    try {
      await deleteGlamour(id);
      toast.success('Glamour gelöscht');
      fetchGlamours();
    } catch (error) {
      console.error('Error deleting glamour:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Löschen des Glamours'));
    }
  };

  const handleRandom = async () => {
    try {
      const data = await getRandomGlamour();
      setRandomGlamour(data);
    } catch (error) {
      console.error('Error getting random glamour:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Laden eines zufälligen Glamours'));
    }
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setFormError(null);
  };

  const resetForm = () => {
    setEditingGlamour(null);
    setFormData({ name: '', occasion: '', character_id: '', items: '', notes: '', image_url: '' });
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1><FaTshirt /> Glamours</h1>
          <div className="header-actions">
            <button className="random-btn" onClick={handleRandom}>
              <FaDice /> Zufällig
            </button>
            <button className="add-btn" onClick={openCreateModal}>
              <FaPlus /> Neues Glamour
            </button>
          </div>
        </div>

        {randomGlamour && (
          <div className="random-glamour-banner">
            <h3>Zufälliges Glamour: {randomGlamour.name}</h3>
            <p>{randomGlamour.occasion && `Anlass: ${randomGlamour.occasion}`}</p>
            <button className="dismiss-btn" onClick={() => setRandomGlamour(null)}>Schließen</button>
          </div>
        )}

        <div className="filter-tabs">
          <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>Alle</button>
          <button className={filter === 'casual' ? 'active' : ''} onClick={() => setFilter('casual')}>Casual</button>
          <button className={filter === 'formal' ? 'active' : ''} onClick={() => setFilter('formal')}>Formal</button>
          <button className={filter === 'combat' ? 'active' : ''} onClick={() => setFilter('combat')}>Kampf</button>
          <button className={filter === 'rp' ? 'active' : ''} onClick={() => setFilter('rp')}>RP</button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="glamours-grid">
            {glamours.length > 0 ? (
              glamours.map(glamour => (
                <div key={glamour.id} className="glamour-card">
                  {glamour.image_url && (
                    <div className="glamour-image">
                      <img src={glamour.image_url} alt={glamour.name} />
                    </div>
                  )}
                  <div className="glamour-body">
                    <div className="glamour-header">
                      <h3 className="glamour-name">{glamour.name}</h3>
                      {glamour.occasion && (
                        <span className={`occasion-badge ${glamour.occasion}`}>{glamour.occasion}</span>
                      )}
                    </div>
                    {glamour.character_id && (
                      <span className="glamour-character">Charakter: {glamour.character_id}</span>
                    )}
                    {glamour.items && (
                      <div className="glamour-items">
                        <span className="items-label">Ausrüstung:</span>
                        <ul>
                          {(Array.isArray(glamour.items) ? glamour.items : [glamour.items]).map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {glamour.notes && <p className="glamour-notes">{glamour.notes}</p>}
                    <div className="glamour-actions">
                      <button className="action-btn edit" onClick={() => handleEdit(glamour)} title="Bearbeiten">
                        <FaEdit />
                      </button>
                      <button className="action-btn delete" onClick={() => handleDelete(glamour.id)} title="Löschen">
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-glamours">Keine Glamours gefunden</div>
            )}
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>{editingGlamour ? 'Glamour bearbeiten' : 'Neues Glamour'}</h2>
              {formError && <div className="shared-form-error-banner">{formError}</div>}
              <form onSubmit={handleCreate}>
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Anlass</label>
                    <select
                      value={formData.occasion}
                      onChange={e => setFormData({...formData, occasion: e.target.value})}
                    >
                      <option value="">-- Wählen --</option>
                      <option value="casual">Casual</option>
                      <option value="formal">Formal</option>
                      <option value="combat">Kampf</option>
                      <option value="rp">RP</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Charakter-ID</label>
                    <input
                      type="text"
                      value={formData.character_id}
                      onChange={e => setFormData({...formData, character_id: e.target.value})}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Ausrüstung (ein Item pro Zeile)</label>
                  <textarea
                    value={formData.items}
                    onChange={e => setFormData({...formData, items: e.target.value})}
                    placeholder="Kopf: Item&#10;Brust: Item&#10;Hände: Item"
                  />
                </div>
                <div className="form-group">
                  <label>Bild-URL</label>
                  <input
                    type="text"
                    value={formData.image_url}
                    onChange={e => setFormData({...formData, image_url: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Notizen</label>
                  <textarea
                    value={formData.notes}
                    onChange={e => setFormData({...formData, notes: e.target.value})}
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeModal} disabled={saving}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={saving}>{saving ? 'Speichert\u2026' : (editingGlamour ? 'Speichern' : 'Erstellen')}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Glamours;
